// Used in student-assignment.component.html, view-assignment.component.html, grade-assignments.component.html

// Angular
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { FileUploader } from "ng2-file-upload";

// Services/Types/Helpers
import { CookieService } from "#services/cookie.service";
import { LocalStorage } from "#services/local-storage.service";
import { SnackBarService } from "#services/snackbar.service";
import { TechCheckService } from "#services/tech-check.service";
import {
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    PPUser as User,
} from "cli-types";

// RXJS/Store
import { Subject, timer } from "rxjs";
import { map, takeWhile } from "rxjs/operators";

// Component
import { Perform, PerformAssignmentPayload, PerformSliders } from "@carnegie-learning/cure-types";
import { AvenueService } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "avenue-eval",
    templateUrl: "./avenue-eval.component.html",
    styleUrls: ["./avenue-eval.component.scss"],
    standalone: false
})
export class AvenueEvalComponent implements OnInit, AfterViewInit, OnDestroy {
    // assignment can be an Assignment or CompositeTaskAssignment
    @Input() assignment: Assignment | CompositeTaskAssignment;
    @Input() userProfile: User;
    @Input() isWorldLanguageProgram: boolean;
    @Input() evaluatorScore: string;

    hasTotalScore = false;
    hasProficiencyScores = false;

    isComposite = false;
    performTask: Perform;

    @ViewChild("video", { static: false }) videoElementRef: ElementRef;

    // Teacher side only: used for singular assignments, composite doesn't need this triggered
    @Output()
    gradingCompleted = new EventEmitter<any>();

    @Output() sendAvenueFeedbackForm = new EventEmitter<UntypedFormGroup>();
    @Output() sendShowUploadedVideo = new EventEmitter<boolean>();
    @Output() sendVideoRecorded = new EventEmitter<boolean>();

    isSecureOrigin: boolean;
    isChromeIpadUser = false;
    isIosMobile: boolean;
    showUploadedVideo = false;
    videoRecorded = false;

    errorMsg: string;

    uploadedFile: File; // for ios and safari

    videoElement: HTMLVideoElement;
    mediaRecorder: MediaRecorder;
    recordedBlobs: Blob[];
    isRecording = false;
    downloadUrl: string;
    stream: MediaStream | null;
    showRubric: boolean;

    sliders: PerformSliders[];

    feedbackForm: UntypedFormGroup = this.fb.group({
        feedback: ["", []],
    });

    videoBuffer: Blob;
    submitClicked = false;
    uploader: FileUploader;

    private countdown = new Subject<number>();
    countdownTimer: number;
    showOverlay = false;

    mimeTypes = ["video/quicktime", "video/mp4", "video/webm"];
    techInfo: any; // The helper that populates this, explicitly returns any.
    browserVersion: number;
    errorSecureVideo = "Video Recording is only supported over HTTPS.";
    errorUnsupportedBrowser =
        "Video Recording is not supported on this browser.";
    errorWebcamUnavailable = "Webcam is unavailable.";

    recordingOpts = {
        audio: {
            echoCancellation: { exact: true },
        },
        video: {
            width: 360,
        },
    };

    constructor(
        private fb: UntypedFormBuilder,
        private cdr: ChangeDetectorRef,
        public localStorage: LocalStorage,
        private techChecker: TechCheckService,
        private avenueTasksService: AvenueService,
        private snackBarService: SnackBarService,
        private cookieService: CookieService
    ) {
        // window.isSecureContext could be used for Chrome
        this.isSecureOrigin =
            location.protocol === "https:" ||
            location.host.includes("localhost");

        // if (!this.isSecureOrigin && !this.isIosMobile) {
        //     this.errorMsg = this.errorSecureVideo;
        // }
    }

    ngOnInit() {
        const that = this;
        this.showRubric = false;
        this.sendAvenueFeedbackForm.emit(this.feedbackForm);
        this.sendVideoRecorded.emit(this.videoRecorded);

        this.isIosMobile = /ipad|iPhone/i.test(
            navigator.userAgent.toLowerCase()
        );

        if (typeof MediaRecorder === "undefined") {
            // MediaRecorder is not supported in Safari and iOS devices :(
            this.isChromeIpadUser = true;
            this.isIosMobile = true;
        }

        /* PAS-7415: Commenting this out for passport/5.12. Try again on passport/6.0, with this new conditional.*/
        this.techInfo = this.techChecker.getDeviceInfo();
        this.browserVersion = Number.parseInt(this.techInfo.browserVersion, 10);
        if (
            this.techInfo.browser.toLowerCase() === "safari" &&
            this.isIosMobile &&
            !MediaRecorder.isTypeSupported("video/webm;codecs=vp9") &&
            !MediaRecorder.isTypeSupported("video/webm;codecs=vp8") &&
            !MediaRecorder.isTypeSupported("video/webm")
        ) {
            this.errorMsg = this.errorUnsupportedBrowser;
        }

        if (this.assignment.hasOwnProperty("compositeTaskAssignmentID")) {
            const assnmnt = <CompositeTaskAssignment>this.assignment;
            this.performTask = <Perform>assnmnt.asset;
            this.isComposite = true;
        } else {
            const assnmnt = <Assignment>this.assignment;
            this.performTask = <Perform>assnmnt.asset;
        }

        this.sliders = [];

        // Flag for conditionally showing new scoring radio buttons
        const newScoring = true;
        //  new Date(this.assignment.startDate) > new Date(2020, 0, 28); // this.assignment.evaluatedAt === null;

        this.sliders.push({ key: "Control", maxVal: 5, isSlider: !newScoring });
        this.sliders.push({ key: "Form", maxVal: 5, isSlider: !newScoring });
        this.sliders.push({ key: "Context", maxVal: 5, isSlider: !newScoring });
        this.sliders.push({
            key: "Comprehensibility",
            maxVal: 5,
            isSlider: !newScoring,
        });
        this.sliders.push({
            key: "Total Activity Score",
            maxVal: 10,
            isSlider: true,
        });

        // Set up the form values, using the existing values from the assignment.
        this.feedbackForm.patchValue({ feedback: this.assignment.feedback });

        let evalScoreArray: Array<string> = [];
        const compositeScore: string | undefined = (<Assignment>this.assignment)
            .compositeScore;
        if (!this.isComposite && compositeScore) {
            evalScoreArray = compositeScore.split(",");
        } else if (
            this.isComposite &&
            (<Assignment>this.assignment).sliderScore
        ) {
            evalScoreArray = (<Assignment>this.assignment).sliderScore.split(
                ","
            );
        } else {
            this.sliders.forEach(function (slider) {
                evalScoreArray.push("0");
            });
        }

        for (let i = 0; i < evalScoreArray.length - 1; i++) {
            if (parseInt(evalScoreArray[i], 10) > 0) {
                this.hasProficiencyScores = true;
            }
        }

        this.hasTotalScore = parseInt(evalScoreArray[4], 10) > 0;

        let i = 0;
        this.sliders.forEach(function (slider) {
            let existingVal = "";
            if (i < evalScoreArray.length) {
                existingVal = evalScoreArray[i];
                slider.val = existingVal;
            }
            that.feedbackForm.addControl(
                slider.key,
                new UntypedFormControl(existingVal)
            );
            i++;
        });

        this.cdr.detectChanges();

        const apiURL =
            (<any>window).apiHost +
            "/api/avenue/assignments/" +
            this.assignment.assignmentID +
            "/upload-file";
        const authToken = this.cookieService.get("CL_TOKEN2");

        this.uploader = new FileUploader({
            url: apiURL,
            authToken: authToken,
            autoUpload: false,
            method: "POST",
        });

        this.uploader.onAfterAddingFile = (item) => {
            item.withCredentials = false;
        };
    }

    getTotalScoreEntered(): boolean {
        return this.feedbackForm.value["Total Activity Score"] > 0;
    }

    ngOnDestroy(): void {
        if (this.stream) {
            // Close the media stream when component is destroyed
            this.stream.getTracks().forEach((track) => track.stop());
            this.stream = null;
        }
    }

    fileSelected() {
        if (this.uploader.queue.length > 0) {
            // If the Queue is greater than 1, remove the first one.
            if (this.uploader.queue.length > 1) {
                this.uploader.queue[0].remove();
            }
        }
    }

    isStudent() {
        return this.userProfile && this.userProfile.roleLevel === 5;
    }

    getMimeTypes() {
        return this.mimeTypes.join(",");
    }

    public onUploaderChange(event: any): void {
        const f = event.srcElement.files[0];

        this.uploadedFile = new File([f], f.name, {
            type: f.type,
        });

        this.videoElement.src = window.URL.createObjectURL(this.uploadedFile);

        this.showUploadedVideo = true;
        this.sendShowUploadedVideo.emit(this.showUploadedVideo);
    }

    getEvaluatorScore() {
        const that = this;
        const scores: string[] = [];

        this.sliders.forEach(function (slider) {
            let score = that.feedbackForm.value[slider.key];
            if (!score) {
                score = 0;
            }
            scores.push(score);
        });
        // If Teacher and no evaluatorScore is set, do not override it, else, use evaluatorScore
        if (!this.isStudent()) {
            if (!this.evaluatorScore || this.evaluatorScore === "") {
                scores[4] = "null";
            } else {
                scores[4] = this.evaluatorScore;
            }
        }
        return scores.join(",");
    }

    async ngAfterViewInit() {
        if (!this.isSecureOrigin && !this.isIosMobile) {
            this.errorMsg = this.errorSecureVideo;
            return;
        }

        if (this.videoElementRef) {
            this.videoElement = this.videoElementRef.nativeElement;
            if (!this.assignment.evaluatorVideoUrl && !this.isStudent()) {
                this.videoElement.muted = true;
                this.videoElement.controls = false;

                if (navigator.mediaDevices) {
                    navigator.mediaDevices
                        .getUserMedia(this.recordingOpts)
                        .then((stream) => {
                            this.stream = stream;
                            this.videoElement.srcObject = this.stream;
                            // If the assignment does not have an existing evaluator video, turn on the recorder.
                        })
                        .catch((error) => {
                            if (!this.isIosMobile) {
                                this.errorMsg = this.errorWebcamUnavailable;
                            }
                            this.cdr.detectChanges();
                        });
                }
            } else {
                this.videoElement.muted = false;
                this.videoElement.controls = true;
            }
        }
    }

    recordingCountdownDelay() {
        if (!this.stream) {
            navigator.mediaDevices
                .getUserMedia(this.recordingOpts)
                .then((stream) => {
                    this.stream = stream;
                    this.startCountdown();
                })
                .catch((error) => {
                    if (!this.isIosMobile) {
                        this.errorMsg = this.errorWebcamUnavailable;
                    }
                    this.cdr.detectChanges();
                });
        } else {
            this.startCountdown();
        }
    }

    startCountdown() {
        const startTime = 5;
        this.showOverlay = true;
        timer(0, 1000)
            .pipe(
                takeWhile((t) => t < startTime),
                map((t) => {
                    this.countdownTimer = startTime - t;
                    // Update the page
                    this.cdr.detectChanges();
                    return this.countdownTimer;
                })
            )
            .subscribe(
                (t) => this.countdown.next(t),
                null,
                () => {
                    this.countdown.complete();

                    this.showOverlay = false;
                    // Countdown complete. Start recording.
                    this.startRecording();

                    this.cdr.detectChanges();
                }
            );
    }

    startRecording() {
        this.videoElement.srcObject = this.stream;
        this.videoElement.controls = false;
        this.videoElement.muted = true;
        this.recordedBlobs = [];

        let options;
        if (this.techInfo.browser === "Firefox" && this.browserVersion >= 71) {
            // Firefox v71+ introduces new mime requirements (PV2-2286)
            // https://github.com/mozilla/hubs/issues/2024
            options = { mimeType: "video/webm;codecs=vp8,opus" };
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
            options = { mimeType: "video/webm; codecs=vp9" };
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
            options = { mimeType: "video/webm; codecs=vp8" };
        } else if (MediaRecorder.isTypeSupported("video/webm")) {
            options = { mimeType: "video/webm" };
        } else {
            if (!this.isIosMobile) {
                this.errorMsg = this.errorUnsupportedBrowser;
            }
        }

        try {
            if (this.stream) {
                this.mediaRecorder = new MediaRecorder(this.stream, options);
            }
        } catch (err) {
            // console.log(err);
        }

        this.mediaRecorder.start(); // collect 100ms of data
        this.isRecording = true;
        this.onDataAvailableEvent();
        this.onStopRecordingEvent();
    }

    stopRecording() {
        this.mediaRecorder.stop();

        this.isRecording = false;
    }

    playRecording() {
        if (!this.recordedBlobs || !this.recordedBlobs.length) {
            return;
        }

        this.videoElement.src = this.downloadUrl;
        this.videoElement.srcObject = null;

        this.videoElement.controls = true;
        this.videoElement.muted = false;
        this.videoElement.play();
    }

    onDataAvailableEvent() {
        try {
            this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data && event.data.size > 0) {
                    this.recordedBlobs.push(event.data);
                }
            };
        } catch (error) {
            // console.log(error);
        }
    }

    onStopRecordingEvent() {
        try {
            this.mediaRecorder.onstop = (event: Event) => {
                this.videoBuffer = new Blob(this.recordedBlobs, {
                    type: "video/webm",
                });
                this.downloadUrl = window.URL.createObjectURL(this.videoBuffer);

                this.cdr.detectChanges();

                // After we stop recording, kick over into play mode.
                this.playRecording();
                this.videoRecorded = true;
                this.cdr.detectChanges();
                this.sendVideoRecorded.emit(this.videoRecorded);
                this.stream?.getTracks().forEach((track) => track.stop());
                this.stream = null;
            };
        } catch (error) {
            // console.log(error);
        }
    }

    taskComplete() {
        if (!this.assignment) {
            // We are in preview mode, do nothing.
            return;
        }

        this.submitClicked = true;

        if (!this.videoBuffer && !this.showUploadedVideo) {
            // Teacher already recorded a video on a previous pass through.
            this.submitFeedbackForm(null);
            return;
        }

        this.uploader.onSuccessItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            const responseObj = JSON.parse(response);

            const evaluatorVideo =
                responseObj.fileName + "." + responseObj.extension;

            this.submitFeedbackForm(evaluatorVideo);
        };

        this.uploader.onErrorItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            // console.log(response);
        };

        if (this.isIosMobile) {
            this.uploader.addToQueue([this.uploadedFile]);
        } else {
            const file = new File([this.videoBuffer], "recording.webm", {
                type: "video/webm",
            });
            this.uploader.addToQueue([file]);
        }

        this.uploader.queue[0].upload();
    }

    submitFeedbackForm(evaluatorVideo: string | null) {
        let includeSliderScores = false;
        this.sliders.forEach((slider) => {
            const score = this.feedbackForm.value[slider.key];
            if (score && score > 0) {
                includeSliderScores = true;
            }
        });

        const payload: any = {
            // TODO: should be something like {[index:string]: string|number}, but submitAvenueAssignment can only accept AvenueAssignmentPayload
            assignmentID: this.assignment.assignmentID,
        };

        if (includeSliderScores) {
            payload["sliderScore"] = this.getEvaluatorScore();
        }

        if (evaluatorVideo != null) {
            payload["evaluatorVideo"] = evaluatorVideo;
        }

        if (this.isComposite) {
            payload["compositeTaskAssignmentID"] = (<CompositeTaskAssignment>(
                this.assignment
            )).compositeTaskAssignmentID;
        }

        this.uploader.clearQueue();
        this.submitAvenueAssignment(payload);
        this.submitClicked = false;
        this.showUploadedVideo = false;
        this.videoRecorded = false;
        this.sendShowUploadedVideo.emit(this.showUploadedVideo);
        Object.keys(this.feedbackForm.controls).forEach((control) => {
            this.feedbackForm.controls[control].markAsPristine();
        });
    }

    submitAvenueAssignment(payload: PerformAssignmentPayload) {
        if (this.isComposite) {
            this.avenueTasksService
                .submitCompositeAvenueAssignment(payload)
                .subscribe(
                    (data) => {
                        this.snackBarService.success("Feedback Submitted!");
                    },
                    (error) => {
                        this.snackBarService.success(
                            "Feedback Submission Failed!"
                        );
                    }
                );
        } else {
            this.avenueTasksService.submitAvenueAssignment(payload).subscribe(
                (data) => {
                    this.snackBarService.success("Feedback Submitted!");
                    this.gradingCompleted.emit();
                },
                (error) => {
                    this.snackBarService.success("Feedback Submission Failed!");
                }
            );
        }
    }
}
